import { Medal } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useLabStages } from "../hooks/useLabStages";
import {
  type LabDetails,
  type LabMember,
  LabService
} from "../services/labService";
import { normalizeLabType } from "../types/lab";
import { cx } from "../utils/functions";

import { Alert } from "../components/Alert";
import { Calculator } from "../components/Calculator";
import { EditLabModal } from "../components/EditLabModal";
import { icons } from "../components/icons";
import { useAuth } from "../contexts/useAuth";


import { CertificationDownloads } from "../components/lab-details/CertificationDownloads";
import { Card } from "../components/ui/Card";
import { highlightBrand } from "../utils/brandUtils";

// Tier type definition
type CertificationTier = "bronze" | "silver" | "gold";

// Tier configuration for UI
const tierConfig = {
  bronze: {
    label: "Bronze",
    bgColor: "bg-amber-100",
    borderColor: "border-amber-500",
    textColor: "text-amber-700",
    buttonActive: "btn-warning",
    baselineDescription: "Calculate your lab's current environmental performance.",
    categories: {
      wet: [
        { key: "waste", title: "Lab Waste", description: "Understand your current lab waste setup and disposal habits." },
        { key: "cold", title: "Cold Storage", description: "Understand how much energy your fridges and freezers are currently using." },
        { key: "computer", title: "Computer Hardware", description: "Understand how efficiently your computers are using energy." },
      ],
      dry: [
        { key: "computer", title: "Computer Energy", description: "Review computing energy use." },
        { key: "data", title: "Data Storage", description: "Assess data management practices." },
        { key: "office", title: "Eco-Office", description: "Evaluate office sustainability." },
      ],
    },
  },
  silver: {
    label: "Silver",
    bgColor: "bg-gray-100",
    borderColor: "border-gray-500",
    textColor: "text-gray-700",
    buttonActive: "btn-neutral",
    baselineDescription: "Calculate your lab's current performance for transport, green chemistry and procurement.",
    categories: {
      wet: [
        { key: "transport", title: "Transport", description: "Check how your team travels for work and commuting." },
        { key: "chemistry", title: "Green Chemistry", description: "Review chemical use, substitution and safer alternatives." },
        { key: "procurement", title: "Procurement", description: "Assess how you purchase consumables." },
      ],
      dry: [
        { key: "transport", title: "Transport", description: "Check how your team travels for work and commuting." },
        { key: "chemistry", title: "Green Chemistry", description: "Review chemical use, substitution and safer alternatives." },
        { key: "procurement", title: "Procurement", description: "Assess how you purchase consumables." },
      ],
    },
  },
  gold: {
    label: "Gold",
    bgColor: "bg-yellow-100",
    borderColor: "border-yellow-500",
    textColor: "text-yellow-700",
    buttonActive: "btn-accent",
    baselineDescription: "Calculate your lab's current performance for eco offices, equipment and water.",
    categories: {
      wet: [
        { key: "offices", title: "Eco Offices", description: "Review office practices that support a low-impact workplace." },
        { key: "equipment", title: "Equipment", description: "Assess how equipment is selected, used and managed." },
        { key: "water", title: "Water", description: "Check how your lab uses and conserves water." },
      ],
      dry: [
        { key: "offices", title: "Eco Offices", description: "Review office practices that support a low-impact workplace." },
        { key: "equipment", title: "Equipment", description: "Assess how equipment is selected, used and managed." },
        { key: "water", title: "Water", description: "Check how your lab uses and conserves water." },
      ],
    },
  },
};

const certificationStateLabels: Record<string, string> = {
  in_progress: "In progress",
  under_review: "Submitted for audit",
  certified: "Certified",
  expired: "Expired",
  withdrawn: "Withdrawn",
};

const formatCertificationStatusLabel = (
  certification: LabDetails["current_certification"]
) => {
  if (!certification) {
    return "Not started";
  }

  if (
    certification.state === "in_progress" &&
    certification.latest_review_request
  ) {
    return "Changes requested";
  }

  return certificationStateLabels[certification.state] ?? certification.state;
};

const formatCertificationLevelLabel = (level: string) => {
  return level.charAt(0).toUpperCase() + level.slice(1);
};

const formatReviewEventAuthor = (
  event: NonNullable<LabDetails["current_certification"]>["review_events"][number]
) => {
  return event.created_by?.name || event.created_by?.email || "Unknown";
};

const formatReviewEventDate = (value: string | null) => {
  if (!value) {
    return null;
  }

  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const AuditStatusPanel = ({ lab }: { lab: LabDetails }) => {
  const certification = lab.current_certification;

  if (!certification) {
    return null;
  }

  const reviewRequest = certification.latest_review_request;
  const reviewEvents = certification.review_events ?? [];
  const hasRequestedChanges =
    certification.state === "in_progress" && reviewRequest;

  return (
    <Card variant="primary">
      <div className="card-body gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-base-content/60">
              Audit status
            </p>
            <h2 className="text-xl font-semibold">
              {formatCertificationLevelLabel(certification.level)} certification
            </h2>
          </div>
          <span className="badge badge-primary badge-lg w-fit">
            {formatCertificationStatusLabel(certification)}
          </span>
        </div>

        <div className="space-y-4">
          {reviewEvents.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-base-content/70">
                Comments
              </p>
              {reviewEvents.map((event) => {
                const dateLabel = formatReviewEventDate(event.created_at);

                return (
                  <div
                    key={event.id}
                    className="rounded-lg border border-base-300 bg-base-100 p-4"
                  >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm font-medium">
                        {formatReviewEventAuthor(event)}
                      </p>
                      {dateLabel && (
                        <p className="text-xs text-base-content/60">
                          {dateLabel}
                        </p>
                      )}
                    </div>
                    {event.comments && (
                      <p className="mt-2 whitespace-pre-wrap text-sm">
                        {event.comments}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {hasRequestedChanges ? (
            <div>
              <p className="text-sm text-base-content/70">
                To send this certification back for audit:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-base-content/80">
                <li>Change the requested answers on the checklist.</li>
                <li>Optionally update answers in the savings assessment.</li>
                <li>Submit the savings assessment again to resubmit the certification.</li>
              </ul>
            </div>
          ) : (
            <p className="text-sm text-base-content/70">
              This certification is {formatCertificationStatusLabel(certification).toLowerCase()}.
            </p>
          )}

        </div>
      </div>
    </Card>
  );
};

export const LabDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { labId } = useParams<{ labId: string }>();
  const [lab, setLab] = useState<LabDetails | null>(null);
  const [members, setMembers] = useState<LabMember[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [showBaselineCalculator, setShowBaselineCalculator] = useState<boolean>(false);
  const [showSavingslineCalculator, setShowSavingslineCalculator] = useState<boolean>(false);
  const [showEditFteModal, setShowEditFteModal] = useState<boolean>(false);

  // Selected tier is backed by the `?tier=` query param so it survives
  // navigation — e.g. returning from a Silver checklist via the browser Back
  // button restores the Silver tab instead of resetting to Bronze.
  const [searchParams, setSearchParams] = useSearchParams();
  const tierParam = searchParams.get("tier");
  const selectedTier: CertificationTier =
    tierParam === "silver" || tierParam === "gold" ? tierParam : "bronze";
  const setSelectedTier = (tier: CertificationTier) => {
    const next = new URLSearchParams(searchParams);
    next.set("tier", tier);
    setSearchParams(next, { replace: true });
  };

  const { isLoading: stagesLoading, data: stagesData, refetch: refetchStages } = useLabStages(labId!, selectedTier);

  const navigateToChecklist = (preview = false) => {
    const certificationId = lab?.certifications?.find(c => c.level === selectedTier)?.id;
    if (!certificationId) {
      // No certification exists for this tier yet (e.g. the level hasn't been
      // started), so there's no checklist to open. Tell the user instead of
      // silently doing nothing.
      toast(`The ${selectedTier} checklist opens once this level has been started.`);
      return;
    }

    navigate(`/certifications/${certificationId}/checklist`, {
      state: preview
        ? { preview: true, previewMessage: stagesData?.checklist.adviceMessage }
        : undefined,
    });
  };

  const fetchLabDetails = async () => {
    if (!labId) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await LabService.getLabDetails(labId);

      const resolvedLab = data.lab ?? null;
      setLab(resolvedLab);

      if (resolvedLab?.is_member) {
        const membersData = await LabService.getLabMembers(labId);
        const resolvedMembers = Array.isArray(membersData.members) ? membersData.members : [];
        setMembers(resolvedMembers);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchLabDetails();
  }, [labId]);

  const handleCalculatorClose = () => {
    setShowBaselineCalculator(false);
    setShowSavingslineCalculator(false);
    void refetchStages();
    void fetchLabDetails();
  };

  if (isLoading || stagesLoading || !stagesData) {
    return (
      <div className="flex items-center justify-center l0-layout">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading lab details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="l0-layout p-4">
        <div className="max-w-4xl mx-auto">
          <Alert type="error" message={error} />
          <div className="mt-4">
            <Link to="/dashboard" className="btn btn-primary">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!lab) {
    return (
      <div className="l0-layout p-4">
        <div className="max-w-4xl mx-auto">
          <Alert type="error" message="Lab not found" />
          <div className="mt-4">
            <Link to="/dashboard" className="btn btn-primary">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  // Multi-tier model: a lab can hold e.g. a certified bronze and an in-flight
  // silver at once, so awarded/awaiting state is derived from the
  // certifications[] array, not a single current_certification.
  const isBronzeAwarded = lab?.certifications?.some(c => c.level === 'bronze' && c.state === 'certified') ?? false;
  const isSilverAwarded = lab?.certifications?.some(c => c.level === 'silver' && c.state === 'certified') ?? false;
  const isGoldAwarded = lab?.certifications?.some(c => c.level === 'gold' && c.state === 'certified') ?? false;

  const isCurrentTierAwarded = lab?.certifications?.some(c => c.level === selectedTier && c.state === 'certified') ?? false;
  const isAwaitingAudit = lab?.certifications?.some(c => c.level === selectedTier && c.state === 'under_review') ?? false;

  // The checklist is certification-scoped. When the selected tier has no
  // certification yet, there is nothing to open, so its buttons are disabled.
  const selectedTierCertificationId = lab?.certifications?.find(c => c.level === selectedTier)?.id;
  const checklistUnavailable = !selectedTierCertificationId;
  const checklistUnavailableTitle = checklistUnavailable
    ? `The ${selectedTier} checklist opens once this level has been started.`
    : undefined;

  // Progression gating:
  // - Silver unlocks once Bronze is awarded (or remains accessible if already awarded)
  // - Gold unlocks once Silver is awarded (or remains accessible if already awarded)
  const isSilverDisabled = !isBronzeAwarded && !isSilverAwarded;
  const isGoldDisabled = !isSilverAwarded && !isGoldAwarded;

  // Show downloads only for the certification tier currently being viewed.
  // A certified Bronze record must not surface its assets on an in-flight
  // Silver or Gold assessment.
  const selectedTierCert = lab?.certifications?.find(
    (c) => c.level === selectedTier && c.state === "certified",
  );

  return (
    <div className="l0-layout p-4">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-amber-50 rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold items-center flex gap-3 flex-wrap">
                {lab.name}{" "}
                <span className="badge badge-md sm:badge-lg badge-neutral badge-ghost font-normal">
                  {lab.type === 'wet' ? 'Wet lab subscription' : lab.type === 'dry' ? 'Dry lab subscription' : normalizeLabType(lab.type)}
                </span>
              </h1>
              {/* Reduced font size for Organisation name to be visually secondary */}
              <p className="text-base-content/60 text-sm mt-1">
                Organisation: {highlightBrand(lab.organisation_name)}
              </p>

              {/* Awaiting Audit Banner (Header) */}
              {isAwaitingAudit && (
                <div className="alert bg-blue-50 border-blue-200 rounded-lg shadow-sm mt-4 py-2 px-3 flex items-center gap-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="stroke-current shrink-0 h-5 w-5 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h3 className="font-bold text-blue-900 text-sm">Awaiting Certification Audit</h3>
                    <p className="text-xs text-blue-800">
                      Steps completed for {selectedTier} certification. Review in progress.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <Link
                to="/user-guide"
                className="btn btn-outline btn-sm"
              >
                View User Guide
              </Link>
              {(lab.is_champion || user?.can_invite || user?.is_organisation_admin) && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setShowEditFteModal(true)}
                >
                  Edit FTE
                </button>
              )}
              <Link to="/dashboard" className="btn btn-neutral btn-sm">
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>

        {/*TODO: Certification Banners temporarily disabled until feature is finalized */}
        {/* <BronzeCertificationBanner visible={isBronzeAwarded && selectedTier === "bronze"} /> */}
        {/* <SilverCertificationBanner visible={isSilverAwarded && selectedTier === "silver"} /> */}
        {/* <GoldCertificationBanner visible={isGoldAwarded && selectedTier === "gold"} /> */}


        {/* Tier Selection Tabs */}
        <div className="flex flex-nowrap justify-center gap-1.5 sm:gap-2 md:gap-3 mb-6 px-2 sm:px-4">
          <button
            className={`
              btn btn-sm sm:btn-md lg:btn-lg
              transition-all duration-300 ease-in-out
              font-medium tracking-wide
              flex-1 max-w-[120px] sm:max-w-[140px] md:max-w-[160px]
              ${selectedTier === "bronze"
                ? "bg-gradient-to-r from-[#E8C4A0] to-[#D4A574] text-[#6B4423] border border-[#D4A574] shadow-md shadow-amber-300/40 scale-[1.02]"
                : "bg-white text-[#8B6F47] border border-[#D4A574] hover:bg-gradient-to-r hover:from-[#F5E6D3] hover:to-[#E8C4A0] hover:shadow-sm hover:shadow-amber-200/30"
              }
            `}
            onClick={() => setSelectedTier("bronze")}
          >
            <span className="flex items-center gap-1 sm:gap-1.5 md:gap-2">
              <Medal className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" strokeWidth={2.5} />
              <span className="text-xs sm:text-sm md:text-base lg:text-lg">Bronze</span>
            </span>
          </button>
          <button
            className={`
              btn btn-sm sm:btn-md lg:btn-lg
              transition-all duration-300 ease-in-out
              font-medium tracking-wide
              flex-1 max-w-[120px] sm:max-w-[140px] md:max-w-[160px]
              ${selectedTier === "silver"
                ? "bg-gradient-to-r from-[#E8E8E8] to-[#C8C8C8] text-[#4A4A4A] border border-[#C8C8C8] shadow-md shadow-gray-300/40 scale-[1.02]"
                : isSilverDisabled
                  ? "bg-white text-[#6B6B6B] border border-[#C8C8C8] opacity-60 cursor-not-allowed"
                  : "bg-white text-[#6B6B6B] border border-[#C8C8C8] hover:bg-gradient-to-r hover:from-[#F5F5F5] hover:to-[#E8E8E8] hover:shadow-sm hover:shadow-gray-200/30"
              }
            `}
            onClick={() => !isSilverDisabled && setSelectedTier("silver")}
            disabled={isSilverDisabled}
          >
            <span className="flex items-center gap-1 sm:gap-1.5 md:gap-2">
              <Medal className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" strokeWidth={2.5} />
              <span className="text-xs sm:text-sm md:text-base lg:text-lg">Silver</span>
            </span>
          </button>
          <button
            className={`
              btn btn-sm sm:btn-md lg:btn-lg
              transition-all duration-300 ease-in-out
              font-medium tracking-wide
              flex-1 max-w-[120px] sm:max-w-[140px] md:max-w-[160px]
              ${selectedTier === "gold"
                ? "bg-gradient-to-r from-[#FFE999] to-[#F4D03F] text-[#8B7500] border border-[#F4D03F] shadow-md shadow-yellow-300/40 scale-[1.02]"
                : isGoldDisabled
                  ? "bg-white text-[#C9A227] border border-[#F4D03F] opacity-60 cursor-not-allowed"
                  : "bg-white text-[#C9A227] border border-[#F4D03F] hover:bg-gradient-to-r hover:from-[#FFF8DC] hover:to-[#FFE999] hover:shadow-sm hover:shadow-yellow-200/30"
              }
            `}
            onClick={() => !isGoldDisabled && setSelectedTier("gold")}
            disabled={isGoldDisabled}
          >
            <span className="flex items-center gap-1 sm:gap-1.5 md:gap-2">
              <Medal className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" strokeWidth={2.5} />
      <span className="text-xs sm:text-sm md:text-base lg:text-lg">Gold</span>
            </span>
          </button>
        </div>

        <AuditStatusPanel lab={lab} />

        {/* Lab Members Section */}
        {lab.is_member && (
          <div className="bg-amber-50 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">Lab Members</h2>
              {(lab.is_champion || user?.can_invite || user?.is_organisation_admin) && (
                <Link
                  to={`/invitations?labId=${lab.id}&type=user`}
                  className="btn btn-primary btn-sm"
                >
                  Invite Members
                </Link>
              )}
            </div>
            {members.length === 0 ? (
              <p className="text-base-content/70">No other members in this lab yet.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="bg-white rounded-lg p-4 shadow-sm"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium truncate mr-2" title={member.email}>
                        {[member.first_name, member.last_name].filter(Boolean).join(' ') || member.email}
                      </span>
                      {member.is_champion && (
                        <div className="badge badge-primary badge-sm whitespace-nowrap">
                          Champion
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-base-content/60 mt-1 truncate">
                      {member.email}
                    </p>
                    {member.last_active && (
                      <p className="text-xs text-base-content/60 mt-1">
                        Last active:{" "}
                        {new Date(member.last_active).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Lab Zero Activities */}
        <Card variant={"primary"}>
          <div className={"card-body"}>
            <div className={"card-title"}>
              {/* Dynamic title based on selected tier */}
              <h2 className={`mb-4 sm:mb-6 text-lg sm:text-xl md:text-2xl ${selectedTier === "gold" ? "text-[#8B7500]" : tierConfig[selectedTier].textColor}`}>
                {tierConfig[selectedTier].label} Certification Steps:
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
              <Card variant={"secondary"}>
                <div className="card-body p-4 sm:p-6">
                  <div className="flex flex-row gap-3 sm:gap-4 items-center">
                    <div className="w-[16px] sm:w-[18px] flex-shrink-0">
                      <img alt={`${tierConfig[selectedTier].label} Calculator Icon`} src={icons.bronze_calculator_icon} className="w-full h-auto" />
                    </div>
                    <h3 className="card-title text-base sm:text-lg leading-tight">1. Baseline Assessment</h3>
                  </div>
                  <p className="text-base-content/70 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">
                    {tierConfig[selectedTier].baselineDescription}
                  </p>
                  <div className="card-actions">
                    {isCurrentTierAwarded ? (
                      <button
                        className="btn btn-primary btn-sm gap-2"
                        onClick={() => {
                          setShowBaselineCalculator(true);
                          setShowSavingslineCalculator(false);
                          setTimeout(() => {
                            document
                              .getElementById("baseline-calculator")
                              ?.scrollIntoView({ behavior: "smooth" });
                          }, 100);
                        }}
                      >
                        Review
                      </button>
                    ) : (
                      <>
                        {stagesData.baselineAssessment.adviceMessage && (
                          <div className="alert bg-green-50 mb-3 sm:mb-4 border-2 border-solid border-green-200 rounded-lg shadow-md flex-col sm:flex-row items-start sm:items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-4 w-4 sm:h-5 sm:w-5 text-green-600" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <span className="text-xs sm:text-sm text-green-800 break-words">{stagesData.baselineAssessment.adviceMessage}</span>
                          </div>
                        )}
                        {["start", "view", "disabled"].includes(
                          stagesData.baselineAssessment.actions,
                        ) && (
                            <button
                              disabled={
                                stagesData.baselineAssessment.actions === "disabled"
                              }
                              className="btn btn-primary btn-sm gap-2"
                              onClick={() => {
                                setShowBaselineCalculator(true);
                                setShowSavingslineCalculator(false);
                                setTimeout(() => {
                                  document
                                    .getElementById("baseline-calculator")
                                    ?.scrollIntoView({ behavior: "smooth" });
                                }, 100);
                              }}
                            >
                              {stagesData.baselineAssessment.actions === "view" ? "View" : "Start"}
                            </button>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </Card>

              {/* Bronze Checklist */}
              <Card variant={"secondary"}>
                <div className="card-body p-4 sm:p-6">
                  <div className="flex flex-row gap-3 sm:gap-4 items-center">
                    <div className="w-[16px] sm:w-[18px] flex-shrink-0">
                      <img src={icons.bronze_checklist_icon} alt={`${tierConfig[selectedTier].label} Checklist`} className="w-full h-auto" />
                    </div>
                    <h3 className="card-title text-base sm:text-lg leading-tight">2. Checklist</h3>
                  </div>
                  {/* Changed text from "questions" to "actions" */}
                  <p className="text-base-content/70 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">
                    Complete sustainability checklist actions.
                  </p>
                  {/* Show unlock message when checklist is locked (preview mode), NOT when completed */}
                  {stagesData.checklist.actions === "preview" && (
                    <div className="alert bg-amber-50 mb-3 sm:mb-4 border-2 border-solid border-[#eaddc9] rounded-lg shadow-md flex-col sm:flex-row items-start sm:items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-5 w-5 sm:h-6 sm:w-6 text-orange-600" fill="none" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-xs sm:text-sm font-medium text-orange-900 break-words">{stagesData.checklist.adviceMessage}</span>
                    </div>
                  )}
                  <div className="card-actions">
                    {isCurrentTierAwarded ? (
                      <button
                        onClick={() => {
                          navigateToChecklist(false);
                        }}
                        className="btn btn-primary btn-sm gap-2"
                      >
                        Review
                      </button>
                    ) : (
                      <>
                        {stagesData.checklist.adviceMessage && stagesData.checklist.actions !== "preview" && (
                          <div className="alert bg-green-50 mb-3 sm:mb-4 border-2 border-solid border-green-200 rounded-lg shadow-md flex-col sm:flex-row items-start sm:items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-4 w-4 sm:h-5 sm:w-5 text-green-600" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <span className="text-xs sm:text-sm text-green-800 break-words">{stagesData.checklist.adviceMessage}</span>
                          </div>
                        )}
                        {/* Removed duplicate preview warning - already shown above */}
                        {stagesData.checklist.actions === "preview" && (
                          <button
                            onClick={() => {
                              navigateToChecklist(true);
                            }}
                            disabled={checklistUnavailable}
                            title={checklistUnavailableTitle}
                            className="btn btn-outline btn-sm gap-2"
                          >
                            Preview
                          </button>
                        )}
                        {["start", "view", "disabled"].includes(
                          stagesData.checklist.actions,
                        ) && (
                            <button
                              disabled={stagesData.checklist.actions === "disabled" || checklistUnavailable}
                              title={checklistUnavailableTitle}
                              onClick={() => {
                                navigateToChecklist(false);
                              }}
                              className="btn btn-primary btn-sm gap-2"
                            >
                              {stagesData.checklist.actions === "view" ? "View" : "Start"}
                            </button>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </Card>

              {/* Savings Assessment */}
              <Card variant={"secondary"}>
                <div className="card-body p-4 sm:p-6">
                  <div className="flex flex-row gap-3 sm:gap-4 items-center">
                    <div className="w-[16px] sm:w-[18px] flex-shrink-0">
                      <img alt={`${tierConfig[selectedTier].label} Savings Icon`} src={icons.bronze_calculator_icon} className="w-full h-auto" />
                    </div>
                    <h3 className="card-title text-base sm:text-lg leading-tight">3. Savings Assessment</h3>
                  </div>
                  <p className="text-base-content/70 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">
                    Calculate potential savings from sustainability actions.
                  </p>
                  <div className="card-actions">
                    {isCurrentTierAwarded ? (
                      <button
                        className="btn btn-primary btn-sm gap-2"
                        onClick={() => {
                          setShowSavingslineCalculator(true);
                          setShowBaselineCalculator(false);
                          setTimeout(() => {
                            document
                              .getElementById("savings-calculator")
                              ?.scrollIntoView({ behavior: "smooth" });
                          }, 100);
                        }}
                      >
                        Review
                      </button>
                    ) : (
                      <>
                        {/* Show advice message (success or instruction) when not locked.
                            "disabled" is treated like "preview" here so a locked Silver
                            savings step doesn't render its notice in the success/green
                            alert below. */}
                        {stagesData.savingsCalculator.adviceMessage && !["preview", "disabled"].includes(stagesData.savingsCalculator.actions) && (
                          <div className="alert bg-green-50 mb-3 sm:mb-4 border-2 border-solid border-green-200 rounded-lg shadow-md flex-col sm:flex-row items-start sm:items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-4 w-4 sm:h-5 sm:w-5 text-green-600" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <span className="text-xs sm:text-sm text-green-800 break-words">{stagesData.savingsCalculator.adviceMessage}</span>
                          </div>
                        )}

                        {/* Show preview mode message (locked) */}
                        {["preview", "disabled"].includes(stagesData.savingsCalculator.actions) && (
                          <div className="alert bg-amber-50 mb-3 sm:mb-4 border-2 border-solid border-[#eaddc9] rounded-lg shadow-md flex-col sm:flex-row items-start sm:items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-5 w-5 sm:h-6 sm:w-6 text-orange-600" fill="none" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span className="text-xs sm:text-sm font-medium text-orange-900 break-words">{stagesData.savingsCalculator.adviceMessage}</span>
                          </div>
                        )}

                        {/* Removed duplicate preview warning - already shown above */}
                        {["start", "view", "disabled"].includes(stagesData.savingsCalculator.actions) && (
                          <button
                            disabled={stagesData.savingsCalculator.actions === "disabled"}
                            className={`btn btn-sm gap-2 ${stagesData.savingsCalculator.actions === "disabled"
                              ? "btn-disabled"
                              : "btn-primary"
                              }`}
                            onClick={() => {
                              setShowSavingslineCalculator(true);
                              setShowBaselineCalculator(false);
                              setTimeout(() => {
                                document
                                  .getElementById("savings-calculator")
                                  ?.scrollIntoView({ behavior: "smooth" });
                              }, 100);
                            }}
                          >
                            {stagesData.savingsCalculator.actions === "view" ? "View" : "Start"}
                          </button>
                        )}
                        {stagesData.savingsCalculator.actions === "preview" && (
                          <button
                            className="btn btn-outline btn-sm gap-2"
                            onClick={() => {
                              setShowSavingslineCalculator(true);
                              setShowBaselineCalculator(false);
                              setTimeout(() => {
                                document.getElementById("savings-calculator")?.scrollIntoView({ behavior: "smooth" });
                              }, 100);
                            }}
                          >
                            Preview
                          </button>
                        )}

                        {/* Prevent resubmission when assessment is waiting for audit */}
                        {stagesData.savingsCalculator.adviceMessage?.includes("waiting") && (
                          <button
                            disabled={true}
                            className="btn btn-disabled btn-sm gap-2"
                          >
                            Submitted
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Card>
            </div>

          </div>
        </Card>

        {/* Certificate and banner downloads for the current certified tier.
            Falls back to the numeric certification id when certificate_id is
            not yet minted (the backend mints it on first download). */}
        {selectedTierCert && (
          <CertificationDownloads
            certificateId={selectedTierCert.certificate_id ?? String(selectedTierCert.id)}
            level={selectedTier}
          />
        )}

        {/* Baseline Calculator Section */}
        <div
          id="baseline-calculator"
          className={cx(
            "md:relative md:left-1/2 md:-translate-x-1/2 md:w-screen md:max-w-[min(calc(100vw-12rem),1920px)]",
          )}
        >
          <Calculator
            type={"baseline"}
            lab={{ ...lab!, type: normalizeLabType(lab!.type) }}
            show={showBaselineCalculator}
            onClose={handleCalculatorClose}
            selectedTier={selectedTier}
          />
        </div>

        <div
          id="savings-calculator"
          className={cx(
            "md:relative md:left-1/2 md:-translate-x-1/2 md:w-screen md:max-w-[min(calc(100vw-12rem),1920px)]",
          )}
        >
          {/* Never mount the savings calculator for a locked stage. The buttons
              above already can't set showSavingslineCalculator when the stage is
              "disabled", but this is a client-facing content leak, so it is
              guarded at the mount too rather than relying on that alone. */}
          {stagesData.savingsCalculator.actions !== "disabled" && (
            <Calculator
              previewMode={stagesData.savingsCalculator.actions === "preview"}
              type={"savings"}
              lab={{ ...lab!, type: normalizeLabType(lab!.type) }}
              show={showSavingslineCalculator}
              onClose={handleCalculatorClose}
              selectedTier={selectedTier}
            />
          )}
        </div>
        
        {/* Edit Lab FTE Modal */}
        {showEditFteModal && lab && (
          <EditLabModal
            lab={lab}
            isOpen={showEditFteModal}
            onClose={() => setShowEditFteModal(false)}
            onSuccess={() => {
              void fetchLabDetails();
            }}
          />
        )}
      </div>
    </div>
  );
};
